import React, { useState, useEffect, useCallback } from "react";
import { login, googleLogin } from "../api";
import {
  requestRecoveryEmailOtp,
  verifyRecoveryEmailOtp,
  resetRecoveredPassword,
} from "../api";
import { setToken } from "../auth";
import { useNavigate, Link } from "react-router-dom";

const GOOGLE_CLIENT_ID =
  "364319276279-j8qdq53tshcbhsbjkbmecbem7tfjacn2.apps.googleusercontent.com";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Login() {
  const navigate = useNavigate();

  // Login state
  const [loginValue, setLoginValue] = useState("");
  const [password, setPassword] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [message, setMessage] = useState("");

  // Screen state: login, request, verify, reset, done
  const [screen, setScreen] = useState("login");

  // Password recovery state
  const [recoveryEmail, setRecoveryEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [recoveryMessage, setRecoveryMessage] = useState("");
  const [recoveryError, setRecoveryError] = useState("");

  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const busy = loading || googleLoading;

  // Common API error message
  const getErrorMessage = (error, fallback) => {
    if (!error?.response) {
      return "Unable to connect to the server. Check your connection.";
    }

    const data = error.response.data;

    if (typeof data === "string" && data.length < 200) {
      return data;
    }

    return data?.message || data?.error || fallback;
  };

  // Normal email/username and password login
  const handleSubmit = async (event) => {
    event.preventDefault();

    if (busy) return;

    if (!loginValue.trim() || !password) {
      setMessage("Please enter your email/username and password.");
      return;
    }

    setMessage("");
    setLoading(true);

    try {
      const response = await login({
        login: loginValue.trim(),
        password,
      });

      const token = response?.token ?? response?.data?.token;

      if (!token) {
        setMessage("The server did not return an authentication token.");
        return;
      }

      await setToken(token);
      navigate("/app");
    } catch (error) {
      console.error("Login failed:", error);
      setMessage(getErrorMessage(error, "Invalid credentials."));
    } finally {
      setLoading(false);
    }
  };

  // Google login
  const handleGoogleCredentialResponse = useCallback(
    async (response) => {
      setMessage("");
      setGoogleLoading(true);

      try {
        const result = await googleLogin(response.credential);
        const token = result?.token ?? result?.data?.token;

        if (!token) {
          setMessage("The server did not return an authentication token.");
          return;
        }

        await setToken(token);
        navigate("/app");
      } catch (error) {
        console.error("Google login failed:", error);
        setMessage(
          getErrorMessage(error, "Google sign-in failed.")
        );
      } finally {
        setGoogleLoading(false);
      }
    },
    [navigate]
  );

  // Initialize Google sign-in when the login screen is visible
  useEffect(() => {
    if (screen !== "login") return;
    if (!window.google?.accounts?.id) return;

    const googleElement = document.getElementById("googleSignInDiv");
    if (!googleElement) return;

    window.google.accounts.id.initialize({
      client_id: GOOGLE_CLIENT_ID,
      callback: handleGoogleCredentialResponse,
    });

    googleElement.innerHTML = "";

    window.google.accounts.id.renderButton(googleElement, {
      theme: "outline",
      size: "large",
      width: 280,
      text: "continue_with",
    });
  }, [screen, handleGoogleCredentialResponse]);

  // Open Forgot Password
  const openForgotPassword = () => {
    if (busy) return;

    setRecoveryEmail(
      loginValue.includes("@") ? loginValue.trim() : ""
    );
    setRecoveryMessage("");
    setRecoveryError("");
    setCode("");
    setResetToken("");
    setNewPassword("");
    setConfirmPassword("");
    setScreen("request");
    setMessage("");
  };

  // Request an email OTP
  const handleRequestCode = async (event) => {
    event.preventDefault();

    const email = recoveryEmail.trim().toLowerCase();

    if (!EMAIL_PATTERN.test(email)) {
      setRecoveryError("Enter a valid email address.");
      return;
    }

    setLoading(true);
    setRecoveryMessage("");
    setRecoveryError("");

    try {
      await requestRecoveryEmailOtp(email);

      setRecoveryEmail(email);
      setCode("");
      setScreen("verify");
      setRecoveryMessage(
        "If this email matches an account, a recovery code will be sent."
      );
    } catch (error) {
      console.error("Recovery code request failed:", error);
      setRecoveryError(
        getErrorMessage(error, "Could not request a code. Please try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  // Verify the six-digit OTP
  const handleVerifyCode = async (event) => {
    event.preventDefault();

    const otp = code.trim();

    if (!/^\d{6}$/.test(otp)) {
      setRecoveryError("Enter the six-digit code from your email.");
      return;
    }

    setLoading(true);
    setRecoveryMessage("");
    setRecoveryError("");

    try {
      const result = await verifyRecoveryEmailOtp(
        recoveryEmail.trim().toLowerCase(),
        otp
      );

      if (!result?.resetToken) {
        setRecoveryError(
          "Verification failed. Please request a new code."
        );
        return;
      }

      setResetToken(result.resetToken);
      setCode("");
      setScreen("reset");
      setRecoveryMessage(
        "Email verified. You can now choose a new password."
      );
    } catch (error) {
      console.error("OTP verification failed:", error);
      setRecoveryError(
        getErrorMessage(
          error,
          "The code is invalid or expired. Request a new code."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Request a replacement code
  const handleResendCode = async () => {
    if (busy) return;

    setLoading(true);
    setRecoveryMessage("");
    setRecoveryError("");

    try {
      await requestRecoveryEmailOtp(
        recoveryEmail.trim().toLowerCase()
      );

      setCode("");
      setRecoveryMessage(
        "If this email matches an account, a new code will be sent."
      );
    } catch (error) {
      console.error("Resending recovery code failed:", error);
      setRecoveryError(
        getErrorMessage(
          error,
          "Could not send another code. Please try again later."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Reset password
  const handleResetPassword = async (event) => {
    event.preventDefault();

    if (newPassword.length < 10 || newPassword.length > 72) {
      setRecoveryError(
        "Password must be between 10 and 72 characters."
      );
      return;
    }

    if (newPassword !== confirmPassword) {
      setRecoveryError("Passwords do not match.");
      return;
    }

    setLoading(true);
    setRecoveryMessage("");
    setRecoveryError("");

    try {
      await resetRecoveredPassword(resetToken, newPassword);

      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
      setCode("");
      setScreen("done");
      setRecoveryMessage(
        "Your password has been reset successfully."
      );
    } catch (error) {
      console.error("Password reset failed:", error);
      setRecoveryError(
        getErrorMessage(
          error,
          "Could not reset the password. Please start again."
        )
      );
    } finally {
      setLoading(false);
    }
  };

  // Return to the login form
  const returnToLogin = () => {
    setScreen("login");
    setPassword("");
    setMessage("");
    setRecoveryMessage("");
    setRecoveryError("");
    setCode("");
    setNewPassword("");
    setConfirmPassword("");
    setResetToken("");
  };

  const goBackFromRecovery = () => {
    if (screen === "verify") {
      setScreen("request");
      setCode("");
    } else if (screen === "reset") {
      setScreen("request");
      setCode("");
      setResetToken("");
      setNewPassword("");
      setConfirmPassword("");
    } else {
      returnToLogin();
    }

    setRecoveryMessage("");
    setRecoveryError("");
  };

  return (
    <div className="so-login-page">
      {/* Scoped SwiftOrder theme — fonts, tokens and ticket styling live
          here so this file can be dropped in on its own. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Inter:wght@400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

        .so-login-page {
          --ink: #1c1a17;
          --paper: #fbf9f4;
          --paper-dim: #f2ede1;
          --chili: #e4572e;
          --chili-dark: #c43f1a;
          --herb: #3f6b4f;
          --herb-dark: #2d4f39;
          --mustard: #f2a93b;
          --stone: #6b6459;
          --stone-light: #a49c8d;
          --line: #ddd4c2;
          --font-display: 'Bebas Neue', 'Arial Narrow', sans-serif;
          --font-body: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          --font-mono: 'JetBrains Mono', 'Courier New', monospace;

          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 24px;
          background: var(--paper);
          background-image: radial-gradient(circle at 1px 1px, var(--line) 1px, transparent 1.6px);
          background-size: 26px 26px;
          font-family: var(--font-body);
          color: var(--ink);
          box-sizing: border-box;
        }

        .so-login-page *,
        .so-login-page *::before,
        .so-login-page *::after {
          box-sizing: border-box;
        }

        .so-login-brand {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          margin-bottom: 22px;
        }

        .so-login-brand-mark {
          font-family: var(--font-mono);
          font-weight: 700;
          font-size: 0.8rem;
          background: var(--ink);
          color: var(--paper);
          width: 34px;
          height: 34px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
        }

        .so-login-brand-name {
          font-family: var(--font-display);
          font-size: 1.6rem;
          letter-spacing: 0.02em;
        }

        .so-login-brand-accent {
          color: var(--chili);
        }

        .so-login-card-wrap {
          width: 100%;
          max-width: 420px;
        }

        .so-login-card {
          position: relative;
          background: #fffdf8;
          border: 1px solid var(--line);
          border-radius: 10px;
          padding: 34px 32px 26px;
          box-shadow: 0 18px 40px -14px rgba(28, 26, 23, 0.35);
        }

        .so-login-card::after {
          content: "";
          position: absolute;
          left: 0;
          right: 0;
          bottom: -1px;
          height: 14px;
          background-image: radial-gradient(circle, var(--paper) 5px, transparent 5.5px);
          background-size: 20px 14px;
          background-repeat: repeat-x;
          background-position: -2px 0;
        }

        .so-login-eyebrow {
          font-family: var(--font-mono);
          font-size: 0.7rem;
          letter-spacing: 0.14em;
          text-transform: uppercase;
          color: var(--chili);
          font-weight: 600;
          display: inline-flex;
          align-items: center;
          gap: 8px;
        }

        .so-login-eyebrow::before {
          content: "";
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: var(--chili);
        }

        .so-login-title {
          font-family: var(--font-display);
          font-weight: 400;
          font-size: 2rem;
          letter-spacing: 0.01em;
          margin: 10px 0 10px;
        }

        .so-login-description {
          color: var(--stone);
          font-size: 0.88rem;
          line-height: 1.6;
          margin: 0 0 22px;
        }

        .so-field {
          display: flex;
          flex-direction: column;
          gap: 6px;
          margin-bottom: 16px;
        }

        .so-field label {
          font-size: 0.78rem;
          font-weight: 700;
          font-family: var(--font-mono);
          letter-spacing: 0.04em;
          color: var(--stone);
        }

        .so-field input {
          width: 100%;
          border: 1px solid var(--line);
          border-radius: 6px;
          padding: 12px 14px;
          font-size: 0.95rem;
          font-family: var(--font-body);
          background: var(--paper);
          color: var(--ink);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease;
        }

        .so-field input:focus {
          border-color: var(--chili);
          box-shadow: 0 0 0 3px rgba(228, 87, 46, 0.15);
        }

        .so-field-password-wrap {
          position: relative;
        }

        .so-field-password-wrap input {
          padding-right: 70px;
        }

        .so-password-toggle {
          position: absolute;
          right: 6px;
          top: 50%;
          transform: translateY(-50%);
          border: none;
          background: transparent;
          color: var(--chili);
          font-family: var(--font-mono);
          font-size: 0.72rem;
          font-weight: 700;
          letter-spacing: 0.04em;
          text-transform: uppercase;
          padding: 6px 8px;
          cursor: pointer;
        }

        .so-password-toggle:hover {
          color: var(--chili-dark);
        }

        .so-submit-btn {
          width: 100%;
          background: var(--chili);
          color: var(--paper);
          border: none;
          border-radius: 6px;
          padding: 13px 0;
          font-weight: 700;
          font-size: 0.95rem;
          cursor: pointer;
          box-shadow: 0 10px 24px -10px rgba(228, 87, 46, 0.6);
          transition: background 0.15s ease, transform 0.1s ease;
          margin-top: 4px;
        }

        .so-submit-btn:hover {
          background: var(--chili-dark);
        }

        .so-submit-btn:active {
          transform: translateY(1px);
        }

        .so-submit-btn:disabled {
          opacity: 0.65;
          cursor: not-allowed;
          transform: none;
        }

        .so-secondary-btn {
          width: 100%;
          background: transparent;
          border: 1px dashed var(--line);
          border-radius: 6px;
          color: var(--herb);
          padding: 12px 0;
          font-weight: 700;
          font-size: 0.88rem;
          cursor: pointer;
          margin-top: 12px;
          transition: border-color 0.15s ease, background 0.15s ease;
        }

        .so-secondary-btn:hover {
          border-color: var(--herb);
          background: #eaf3ee;
        }

        .so-secondary-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .so-link-btn {
          border: none;
          padding: 0;
          background: transparent;
          color: var(--chili);
          font-weight: 700;
          cursor: pointer;
          font-family: inherit;
          font-size: 0.85rem;
        }

        .so-link-btn:hover {
          color: var(--chili-dark);
          text-decoration: underline;
        }

        .so-forgot-row {
          text-align: right;
          margin: -8px 0 16px;
        }

        .so-back-btn {
          display: inline-flex;
          gap: 7px;
          align-items: center;
          border: none;
          background: transparent;
          padding: 0;
          margin-bottom: 20px;
          cursor: pointer;
          color: var(--stone);
          font: inherit;
          font-family: var(--font-mono);
          font-size: 0.78rem;
          font-weight: 700;
          letter-spacing: 0.03em;
          text-transform: uppercase;
        }

        .so-back-btn:hover {
          color: var(--chili);
        }

        .so-message {
          padding: 12px 14px;
          border-radius: 6px;
          margin: 4px 0 16px;
          font-size: 0.84rem;
          line-height: 1.5;
          overflow-wrap: anywhere;
        }

        .so-error {
          color: var(--chili-dark);
          background: #fdece6;
          border: 1px solid var(--chili);
        }

        .so-success {
          color: var(--herb-dark);
          background: #eaf3ee;
          border: 1px solid #bfe0cd;
        }

        .so-divider {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 22px 0 16px;
        }

        .so-divider-line {
          flex: 1;
          height: 1px;
          background: var(--line);
        }

        .so-divider-text {
          font-family: var(--font-mono);
          font-size: 0.7rem;
          letter-spacing: 0.1em;
          color: var(--stone-light);
        }

        .so-google-wrap {
          display: flex;
          justify-content: center;
          min-height: 42px;
          margin-bottom: 4px;
        }

        .so-google-status {
          text-align: center;
          color: var(--stone);
          font-size: 0.82rem;
          margin: 10px 0 0;
        }

        .so-register-block {
          text-align: center;
          padding-top: 18px;
          margin-top: 18px;
          border-top: 1px dashed var(--line);
        }

        .so-register-text {
          font-size: 0.88rem;
          color: var(--stone);
          margin: 0 0 6px;
        }

        .so-register-link {
          display: inline-block;
          font-weight: 700;
          font-size: 0.9rem;
          color: var(--chili);
          text-decoration: none;
        }

        .so-register-link:hover {
          color: var(--chili-dark);
          text-decoration: underline;
        }

        @media (max-width: 480px) {
          .so-login-page {
            padding: 14px;
          }

          .so-login-card {
            padding: 26px 20px 22px;
          }
        }
      `}</style>

      <div className="so-login-card-wrap">
        <div className="so-login-brand">
          <span className="so-login-brand-mark">SO</span>
          <span className="so-login-brand-name">
            Swift<span className="so-login-brand-accent">Order</span>
          </span>
        </div>

        <div className="so-login-card">
          {/* NORMAL LOGIN */}
          {screen === "login" && (
            <>
              <span className="so-login-eyebrow">Welcome back</span>
              <h2 className="so-login-title">Log in to SwiftOrder</h2>
              <p className="so-login-description">
                Enter your credentials to continue.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="so-field">
                  <label htmlFor="loginValue">Email or username</label>
                  <input
                    id="loginValue"
                    value={loginValue}
                    onChange={(event) => setLoginValue(event.target.value)}
                    autoComplete="username"
                    autoCapitalize="none"
                    required
                  />
                </div>

                <div className="so-field">
                  <label htmlFor="loginPassword">Password</label>
                  <div className="so-field-password-wrap">
                    <input
                      id="loginPassword"
                      type={passwordVisible ? "text" : "password"}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="so-password-toggle"
                      onClick={() => setPasswordVisible((v) => !v)}
                      tabIndex={-1}
                    >
                      {passwordVisible ? "Hide" : "Show"}
                    </button>
                  </div>
                </div>

                <div className="so-forgot-row">
                  <button
                    className="so-link-btn"
                    type="button"
                    onClick={openForgotPassword}
                  >
                    Forgot password?
                  </button>
                </div>

                <button
                  type="submit"
                  className="so-submit-btn"
                  disabled={busy}
                >
                  {loading ? "Signing in..." : "Log in"}
                </button>
              </form>

              {message && (
                <div className="so-message so-error" role="alert">
                  {message}
                </div>
              )}

              <div className="so-divider">
                <div className="so-divider-line" />
                <span className="so-divider-text">OR</span>
                <div className="so-divider-line" />
              </div>

              <div className="so-google-wrap">
                <div id="googleSignInDiv" />
              </div>

              {googleLoading && (
                <p className="so-google-status">
                  Signing in with Google...
                </p>
              )}

              <div className="so-register-block">
                <p className="so-register-text">
                  Don&apos;t have an account?
                </p>
                <Link to="/register" className="so-register-link">
                  Create an account
                </Link>
              </div>
            </>
          )}

          {/* REQUEST RECOVERY CODE */}
          {screen === "request" && (
            <>
              <button
                type="button"
                className="so-back-btn"
                onClick={returnToLogin}
              >
                ← Back to login
              </button>

              <span className="so-login-eyebrow">Account recovery</span>
              <h2 className="so-login-title">Forgot password?</h2>
              <p className="so-login-description">
                Enter the email address used to sign in to your SwiftOrder
                account. We will send a verification code if the account
                qualifies for recovery.
              </p>

              {recoveryMessage && (
                <div className="so-message so-success" role="status">
                  {recoveryMessage}
                </div>
              )}

              {recoveryError && (
                <div className="so-message so-error" role="alert">
                  {recoveryError}
                </div>
              )}

              <form onSubmit={handleRequestCode}>
                <div className="so-field">
                  <label htmlFor="recoveryEmail">Login email</label>
                  <input
                    id="recoveryEmail"
                    type="email"
                    value={recoveryEmail}
                    onChange={(event) =>
                      setRecoveryEmail(event.target.value)
                    }
                    autoComplete="email"
                    autoCapitalize="none"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="so-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Sending..." : "Send verification code"}
                </button>
              </form>
            </>
          )}

          {/* VERIFY OTP */}
          {screen === "verify" && (
            <>
              <button
                type="button"
                className="so-back-btn"
                onClick={goBackFromRecovery}
              >
                ← Back
              </button>

              <span className="so-login-eyebrow">Verify email</span>
              <h2 className="so-login-title">Enter your code</h2>
              <p className="so-login-description">
                Enter the six-digit code sent to {recoveryEmail}.
              </p>

              {recoveryMessage && (
                <div className="so-message so-success" role="status">
                  {recoveryMessage}
                </div>
              )}

              {recoveryError && (
                <div className="so-message so-error" role="alert">
                  {recoveryError}
                </div>
              )}

              <form onSubmit={handleVerifyCode}>
                <div className="so-field">
                  <label htmlFor="recoveryCode">Six-digit code</label>
                  <input
                    id="recoveryCode"
                    value={code}
                    onChange={(event) =>
                      setCode(
                        event.target.value.replace(/\D/g, "").slice(0, 6)
                      )
                    }
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="so-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Verifying..." : "Verify code"}
                </button>
              </form>

              <button
                type="button"
                className="so-secondary-btn"
                onClick={handleResendCode}
                disabled={loading}
              >
                {loading ? "Please wait..." : "Send a new code"}
              </button>
            </>
          )}

          {/* RESET PASSWORD */}
          {screen === "reset" && (
            <>
              <button
                type="button"
                className="so-back-btn"
                onClick={goBackFromRecovery}
              >
                ← Back
              </button>

              <span className="so-login-eyebrow">Secure your account</span>
              <h2 className="so-login-title">Choose a new password</h2>
              <p className="so-login-description">
                Use a password between 10 and 72 characters.
              </p>

              {recoveryMessage && (
                <div className="so-message so-success" role="status">
                  {recoveryMessage}
                </div>
              )}

              {recoveryError && (
                <div className="so-message so-error" role="alert">
                  {recoveryError}
                </div>
              )}

              <form onSubmit={handleResetPassword}>
                <div className="so-field">
                  <label htmlFor="newPassword">New password</label>
                  <input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(event) =>
                      setNewPassword(event.target.value)
                    }
                    autoComplete="new-password"
                    minLength={10}
                    maxLength={72}
                    required
                  />
                </div>

                <div className="so-field">
                  <label htmlFor="confirmPassword">
                    Confirm new password
                  </label>
                  <input
                    id="confirmPassword"
                    type="password"
                    value={confirmPassword}
                    onChange={(event) =>
                      setConfirmPassword(event.target.value)
                    }
                    autoComplete="new-password"
                    minLength={10}
                    maxLength={72}
                    required
                  />
                </div>

                <button
                  type="submit"
                  className="so-submit-btn"
                  disabled={loading}
                >
                  {loading ? "Updating..." : "Reset password"}
                </button>
              </form>
            </>
          )}

          {/* SUCCESS */}
          {screen === "done" && (
            <>
              <span className="so-login-eyebrow">Completed</span>
              <h2 className="so-login-title">Password updated</h2>
              <p className="so-login-description">
                You can now sign in with your new password.
              </p>

              <div className="so-message so-success" role="status">
                {recoveryMessage ||
                  "Your password has been reset successfully."}
              </div>

              <button
                type="button"
                className="so-submit-btn"
                onClick={returnToLogin}
              >
                Return to login
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
